import { useContent } from "@/content/ContentProvider";
import ContentBlocks from "@/content/ContentBlocks";
import { ContentLoading } from "@/content/ContentProvider";
import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Link2 } from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/site/PageHero";
import ShaderBackground from "@/components/site/ShaderBackground";
import { Reveal, Pill } from "@/components/site/Reveal";
import { PillButton, ImageReveal, Parallax } from "@/components/site/Kit";
import { PaperCard } from "@/components/site/HomeBottom";
import AppPreview from "@/components/site/AppPreview";
import NotFound from "@/pages/NotFound";
import { scorecardForCategory } from "@/lib/scorecard";
function BigImage({ src, alt, caption }) {
  return (
    <section className="px-3 pt-3 sm:px-5">
      <ImageReveal className="nm-img-zoom relative mx-auto max-w-[1400px] overflow-hidden rounded-[24px] bg-[#f7f7f7]">
        <div className="h-[340px] overflow-hidden sm:h-[560px]">
          <Parallax>
            <img
              src={src}
              alt={alt}
              className="h-full w-full object-cover grayscale"
            />
          </Parallax>
        </div>
        {caption && (
          <span className="absolute bottom-4 left-4 rounded-full bg-white px-4 py-2 text-[12px] font-medium">
            {caption}
          </span>
        )}
      </ImageReveal>
    </section>
  );
}
function Row({ label, children }) {
  return (
    <Reveal className="grid gap-4 border-t border-black/10 py-10 lg:grid-cols-12">
      <p className="text-[12px] opacity-50 lg:col-span-4">{label}</p>
      <div className="lg:col-span-7">{children}</div>
    </Reveal>
  );
}
export function AudienceDetail() {
  const { text: cmsText, AUDIENCES, loading } = useContent();
  const { slug } = useParams();
  const idx = AUDIENCES.findIndex((a) => a.slug === slug);
  const a = AUDIENCES[idx];
  useEffect(() => {
    if (a) document.title = `${a.name} | Nomus`;
  }, [a]);
  if (!a) return loading ? <ContentLoading /> : <NotFound />;
  const others = AUDIENCES.filter(
    (x) => x.slug !== a.slug && x.visibility !== "unlisted",
  );
  const next = others[0];
  const contact = `/contact?audience=${encodeURIComponent(a.slug)}`;
  const quiz = scorecardForCategory(a.slug);
  return (
    <>
      <PageHero
        label={cmsText("Detail.audience-label", {
          audience: a.name.toLowerCase(),
        })}
        title={a.title}
        text={a.line}
      />
      {quiz && (
        <section className="px-3 pt-3 sm:px-5" data-testid="category-quiz">
          <div className="mx-auto flex max-w-[1400px] flex-col gap-5 rounded-[28px] bg-black p-6 text-white sm:flex-row sm:items-center sm:justify-between sm:p-8">
            <div>
              <p className="text-[12px] text-white/50">Free · 60 seconds</p>
              <p className="nm-title mt-2 text-[24px] leading-[1.1] sm:text-[32px]">
                {quiz.headline}
              </p>
            </div>
            <PillButton to={quiz.path} variant="inverse" size="lg">
              See your score
            </PillButton>
          </div>
        </section>
      )}
      <section className="px-5 pt-3">
        <div className="mx-auto grid max-w-[1400px] grid-cols-2 md:grid-cols-4">
          {[
            [cmsText("Detail.AudienceDetail.for.f78806"), a.name],
            [
              cmsText("Detail.AudienceDetail.starting-point.79a43d"),
              cmsText("Detail.AudienceDetail.one-workflow.3d650a"),
            ],
            [
              cmsText("Detail.AudienceDetail.delivery.9631af"),
              a.delivery ||
                cmsText("Detail.AudienceDetail.design-to-handover.baa5cd"),
            ],
            [
              cmsText("Detail.AudienceDetail.status.bae7d5"),
              a.status ||
                cmsText("Detail.AudienceDetail.illustrative-engagement.4c415a"),
            ],
          ].map(([k, v]) => (
            <div key={k} className="border-t border-black/10 py-5 pr-4">
              <p className="text-[12px] opacity-40">{k}</p>
              <p className="mt-1 text-[16px] tracking-[-0.01em]">{v}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="px-3 pt-3 sm:px-5">
        <Reveal className="mx-auto min-h-[400px] max-w-[1400px] overflow-hidden rounded-[24px] sm:h-[560px] [&>*]:min-h-[400px]">
          <AppPreview slug={a.slug} steps={a.steps} />
        </Reveal>
      </section>
      <section className="px-5 pt-20">
        <div className="mx-auto max-w-[1400px]">
          <Row
            label={cmsText(
              "Detail.AudienceDetail.where-work-gets-stuck.a0e278",
            )}
          >
            <div className="space-y-5">
              <p className="text-[22px] leading-[1.25] tracking-[-0.02em] sm:text-[28px]">
                {a.intro}
              </p>
              <p className="text-[17px] leading-[1.45] opacity-60">
                {a.situation}
              </p>
            </div>
          </Row>
          <Row
            label={cmsText("Detail.workflow-label", { workflow: a.workflow })}
          >
            <ul className="space-y-2">
              {a.build.map((x, i) => (
                <li
                  key={x}
                  className="flex items-start gap-4 rounded-[20px] bg-[#f7f7f7] px-5 py-3.5 text-[15px] leading-[1.4]"
                >
                  <span className="mt-0.5 text-[12px] tabular-nums opacity-40">
                    0{i + 1}
                  </span>
                  {x}
                </li>
              ))}
            </ul>
          </Row>
          <Row label={cmsText("Detail.AudienceDetail.where-nomus-fits.90f95f")}>
            <p className="text-[22px] leading-[1.3] tracking-[-0.02em]">
              {a.fit}
            </p>
          </Row>
          <Row
            label={cmsText(
              "Detail.AudienceDetail.how-we-would-measure-improvement.b2c8bd",
            )}
          >
            <div className="grid gap-6 sm:grid-cols-3">
              {a.measures.map(([v, l]) => (
                <div key={v}>
                  <h3 className="nm-title text-[28px] leading-[1.1] sm:text-[32px]">
                    {v}
                  </h3>
                  <p className="mt-3 text-[14px] leading-[1.35] opacity-60">
                    {l}
                  </p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-[13px] opacity-50">
              {cmsText(
                "Detail.AudienceDetail.measures-are-agreed-against-your-current-proc.3f02e6",
              )}
            </p>
          </Row>
        </div>
      </section>
      {a.body?.length > 0 && (
        <section className="mx-auto max-w-[900px] px-5 pt-16">
          <ContentBlocks blocks={a.body} />
        </section>
      )}
      <section className="px-3 pt-16 sm:px-5">
        <Reveal className="relative mx-auto max-w-[1400px] overflow-hidden rounded-[28px] bg-black p-8 text-white sm:p-14">
          <ShaderBackground dark strength={0.4} lines={12} fadeLeft={0.4} />
          <div className="relative z-10">
            <h2 className="nm-title max-w-[900px] text-[28px] leading-[1.08] sm:text-[48px]">
              {a.ctaTitle ||
                cmsText(
                  "Detail.AudienceDetail.start-with-the-bottleneck-you-can-name-.f9567b",
                )}
            </h2>
            <p className="mt-6 max-w-[720px] text-[17px] leading-[1.45] text-white/70">
              {a.boundary}
            </p>
            <div className="mt-8">
              <div className="flex flex-wrap gap-3">
                <PillButton to={contact} variant="inverse" size="lg">
                  {a.ctaLabel ||
                    cmsText(
                      "Detail.AudienceDetail.discuss-this-workflow.d27577",
                    )}
                </PillButton>
                {quiz && (
                  <PillButton
                    to={quiz.path}
                    size="lg"
                    className="ring-1 ring-white/30"
                  >
                    See your score
                  </PillButton>
                )}
              </div>
            </div>
          </div>
        </Reveal>
      </section>
      {next && (
        <section className="px-5 pt-16">
          <Link
            to={`/for/${next.slug}`}
            data-cursor="Explore"
            className="group mx-auto flex max-w-[1400px] items-center justify-between gap-6 rounded-[28px] bg-[#f7f7f7] p-6 transition-colors duration-500 hover:bg-black hover:text-white sm:gap-10 sm:p-12"
          >
            <div className="min-w-0 max-w-[1000px]">
              <p className="text-[12px] opacity-50">
                {cmsText("Detail.AudienceDetail.also-for.808e70")}{" "}
                {next.name.toLowerCase()}
              </p>
              <p className="nm-title mt-3 text-[28px] leading-[1.05] sm:text-[52px]">
                {next.title}
              </p>
            </div>
            <ArrowUpRight
              size={36}
              className="shrink-0 transition-transform duration-500 group-hover:rotate-45"
            />
          </Link>
        </section>
      )}
    </>
  );
}
export function InsightDetail() {
  const { text: cmsText, PAPERS, loading, PAPER_POINTS, IMG } = useContent();
  const { id } = useParams();
  const idx = PAPERS.findIndex((p) => p.id === id);
  const p = PAPERS[idx];
  useEffect(() => {
    if (p) document.title = `${p.title} | Nomus`;
  }, [p]);
  if (!p) return loading ? <ContentLoading /> : <NotFound />;
  const points = PAPER_POINTS[p.cat] || [];
  const more = PAPERS.filter(
    (x) => x.id !== p.id && x.visibility !== "unlisted",
  ).slice(0, 3);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(cmsText("Detail.link-copied"));
    } catch {
      toast.error(cmsText("Detail.link-copy-failed"));
    }
  };
  return (
    <>
      <PageHero
        label={cmsText("Detail.note-label", { category: p.cat })}
        title={p.title}
        text={cmsText("Detail.read-time", { minutes: p.read })}
      />
      {p.img && (
        <BigImage src={IMG[p.img] || p.img} alt={p.imageAlt || p.title} />
      )}
      <section className="px-5 pt-20">
        <div className="mx-auto grid max-w-[1400px] gap-10 lg:grid-cols-12">
          <aside className="lg:col-span-3">
            <div className="sticky top-28 space-y-6 text-[13px]">
              <div>
                <p className="opacity-40">
                  {cmsText("Detail.InsightDetail.category.a3c686")}
                </p>
                <Link to={`/insights/category/${p.categories[0]}`}>
                  <Pill className="mt-2">{p.cat}</Pill>
                </Link>
              </div>
              <div>
                <p className="opacity-40">
                  {cmsText("Detail.InsightDetail.format.041a5d")}
                </p>
                <p className="mt-1">
                  {cmsText("Detail.InsightDetail.practical-note.e35798")}
                </p>
              </div>
              <div>
                <p className="opacity-40">
                  {cmsText("Detail.InsightDetail.written-by.d04c53")}
                </p>
                <p className="mt-1">{p.author}</p>
              </div>
              <button
                type="button"
                onClick={copy}
                data-testid="copy-link"
                className="group/roll flex h-9 items-center gap-2 rounded-full bg-[#f7f7f7] px-4 text-[12px] font-medium transition-colors hover:bg-black hover:text-white"
              >
                <Link2 size={13} />
                {cmsText("Detail.InsightDetail.copy-link.3c5f53")}
              </button>
            </div>
          </aside>
          <article className="lg:col-span-7 lg:col-start-5">
            <Reveal>
              <p className="nm-title text-[26px] leading-[1.15] sm:text-[36px]">
                {p.text}
              </p>
            </Reveal>
            {p.body && (
              <div className="mt-12">
                <ContentBlocks blocks={p.body} />
              </div>
            )}
            {points.map(([h, t], i) => (
              <Reveal key={h} className="mt-14 border-t border-black/10 pt-8">
                <p className="text-[12px] tabular-nums opacity-40">0{i + 1}</p>
                <h2 className="mt-2 text-[28px] leading-[1.1] tracking-[-0.03em]">
                  {h}
                </h2>
                <p className="mt-4 text-[18px] leading-[1.45] tracking-[-0.01em] opacity-70">
                  {t}
                </p>
              </Reveal>
            ))}
            <Reveal className="mt-16 rounded-[24px] bg-[#f7f7f7] p-8">
              <p className="nm-title text-[24px] leading-[1.15]">
                {cmsText(
                  "Detail.InsightDetail.want-this-applied-to-your-company-.26a49e",
                )}
              </p>
              <div className="mt-6">
                <PillButton to="/contact">
                  {cmsText("Detail.InsightDetail.discuss-your-workflow.184fa5")}
                </PillButton>
              </div>
            </Reveal>
          </article>
        </div>
      </section>
      <section className="px-5 pt-28">
        <div className="mx-auto max-w-[1400px]">
          <div className="flex items-center justify-between">
            <h2 className="nm-title text-[32px] sm:text-[48px]">
              {cmsText("Detail.InsightDetail.keep-reading.9753f5")}
            </h2>
            <Link
              to="/insights"
              className="nm-link flex items-center gap-2 text-[13px] font-medium"
            >
              <ArrowLeft size={13} />
              {cmsText("Detail.InsightDetail.all-notes.d27576")}
            </Link>
          </div>
          <div className="mt-10 grid gap-x-3 gap-y-10 md:grid-cols-3">
            {more.map((m) => (
              <PaperCard key={m.id} p={m} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
